import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  ArrowsUpDownIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import ActionToolbar from '../ui/ActionToolbar';
import IconActionButton from '../ui/IconActionButton';
import ResponsiveModal from '../ui/ResponsiveModal';
import { Button } from '../ui/Button';
import { Label } from '../ui/Label';
import Select from 'react-select';
import { ticketsAPI, referenceAPI, usersAPI } from '../../api/api';

/** Z-index + inherit text color so Tailwind classNames (not default #333) apply in light/dark */
const subTicketSelectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 10060 }),
  menu: (base) => ({ ...base, zIndex: 10060 }),
  control: (base) => ({
    ...base,
    backgroundColor: 'transparent',
    color: 'inherit',
  }),
  singleValue: (base) => ({ ...base, color: 'inherit' }),
  input: (base) => ({ ...base, color: 'inherit' }),
  placeholder: (base) => ({ ...base, color: 'inherit' }),
  valueContainer: (base) => ({ ...base, color: 'inherit' }),
  option: (base) => ({ ...base, color: 'inherit' }),
};

const subTicketSelectClassNames = {
  control: ({ isDisabled }) =>
    [
      '!min-h-[32px] !min-w-0 !rounded-md !border !shadow-none !text-[11px]',
      '!border-gray-300 !bg-white !text-gray-900',
      'dark:!border-gray-600 dark:!bg-gray-700 dark:!text-gray-100',
      isDisabled ? '!opacity-60' : '',
    ]
      .filter(Boolean)
      .join(' '),
  valueContainer: () => '!text-gray-900 dark:!text-gray-100',
  singleValue: () => '!text-gray-900 dark:!text-gray-100',
  input: () => '!text-gray-900 dark:!text-gray-100',
  placeholder: () => '!text-gray-500 dark:!text-gray-400',
  menu: () =>
    '!z-[10060] !overflow-hidden !rounded-md !border !border-gray-200 !bg-white !text-[11px] !shadow-lg dark:!border-gray-600 dark:!bg-gray-800',
  menuList: () => '!py-1 !text-gray-900 dark:!text-gray-100',
  option: ({ isFocused, isSelected }) =>
    [
      '!cursor-pointer !text-[11px] !text-gray-900 dark:!text-gray-100',
      isFocused ? '!bg-gray-100 dark:!bg-gray-700' : '',
      isSelected ? '!bg-blue-50 dark:!bg-blue-900/30' : '',
    ]
      .filter(Boolean)
      .join(' '),
  noOptionsMessage: () => '!text-xs !text-gray-500 dark:!text-gray-400',
  loadingMessage: () => '!text-xs !text-gray-500 dark:!text-gray-400',
  dropdownIndicator: () => '!text-gray-500 dark:!text-gray-400',
  clearIndicator: () => '!text-gray-500 hover:!text-gray-700 dark:!text-gray-400 dark:hover:!text-gray-200',
  indicatorSeparator: () => '!bg-gray-300 dark:!bg-gray-600',
};

function userLabel(u) {
  if (!u) return '';
  return (
    u.full_name ||
    `${u.first_name || ''} ${u.last_name || ''}`.trim() ||
    u.email ||
    String(u.id)
  );
}

function ticketFromResponse(res) {
  if (!res) return null;
  if (res.success && res.data && typeof res.data === 'object' && !Array.isArray(res.data)) {
    return res.data;
  }
  const { success, message, timestamp, ...rest } = res;
  return rest.id ? rest : null;
}

const SubTicketsTab = ({
  parentTicket,
  projectId,
  onRefreshParent,
  onOpenChildTicket,
  t,
}) => {
  const [sorting, setSorting] = useState([]);
  const [rows, setRows] = useState([]);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [priorities, setPriorities] = useState([]);
  const [statuses, setStatuses] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingSub, setEditingSub] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formPriorityId, setFormPriorityId] = useState('');
  const [formStatusId, setFormStatusId] = useState('');
  const [formAssigneeId, setFormAssigneeId] = useState('');
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [tableSearch, setTableSearch] = useState('');

  const loadUsersForProject = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoadingUsers(true);
      const response = await referenceAPI.getUsersByProjectDepartment(projectId);
      if (response?.success && response?.data) {
        setAvailableUsers(response.data || []);
      } else {
        const usersResponse = await usersAPI.getUsers();
        const usersList = usersResponse.results || usersResponse.data || (Array.isArray(usersResponse) ? usersResponse : []);
        setAvailableUsers(Array.isArray(usersList) ? usersList : []);
      }
    } catch {
      try {
        const usersResponse = await usersAPI.getUsers();
        const usersList = usersResponse.results || usersResponse.data || (Array.isArray(usersResponse) ? usersResponse : []);
        setAvailableUsers(Array.isArray(usersList) ? usersList : []);
      } catch {
        setAvailableUsers([]);
      }
    } finally {
      setLoadingUsers(false);
    }
  }, [projectId]);

  const loadMeta = useCallback(async () => {
    if (!projectId) return;
    try {
      const [prRes, stRes] = await Promise.all([
        referenceAPI.getPriorities(),
        ticketsAPI.getStatusesByProject(projectId, true),
      ]);
      const prData = prRes?.success ? prRes.data : prRes;
      const stData = stRes?.success ? stRes.data : stRes;
      setPriorities(Array.isArray(prData) ? prData : []);
      const stList = Array.isArray(stData) ? stData : [];
      setStatuses(stList.filter((s) => s.show_on_board !== false).sort((a, b) => (a.order_index || 0) - (b.order_index || 0)));
    } catch (e) {
      setPriorities([]);
      setStatuses([]);
    }
  }, [projectId]);

  const refreshChildren = useCallback(async () => {
    if (!parentTicket?.id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await ticketsAPI.getTicket(parentTicket.id);
      const full = ticketFromResponse(res);
      if (full && onRefreshParent) {
        onRefreshParent(full);
      }
      setRows(Array.isArray(full?.child_tickets) ? full.child_tickets : []);
    } catch (e) {
      setError(e.message || t('errorOccurred'));
      setRows(Array.isArray(parentTicket.child_tickets) ? parentTicket.child_tickets : []);
    } finally {
      setLoading(false);
    }
  }, [parentTicket?.id, parentTicket?.child_tickets, onRefreshParent, t]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  useEffect(() => {
    loadUsersForProject();
  }, [loadUsersForProject]);

  useEffect(() => {
    setSelectedRowId(null);
    setTableSearch('');
  }, [parentTicket?.id]);

  const filteredRows = useMemo(() => {
    const q = tableSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const title = (r.title || '').toLowerCase();
      const st = (r.status?.status_name || '').toLowerCase();
      const pr = (r.priority?.priority_name || '').toLowerCase();
      const asg = r.assigned_to_user ? userLabel(r.assigned_to_user).toLowerCase() : '';
      const idStr = String(r.id);
      return (
        title.includes(q) ||
        st.includes(q) ||
        pr.includes(q) ||
        asg.includes(q) ||
        idStr.includes(q)
      );
    });
  }, [rows, tableSearch]);

  useEffect(() => {
    if (selectedRowId && !filteredRows.some((r) => r.id === selectedRowId)) {
      setSelectedRowId(null);
    }
  }, [filteredRows, selectedRowId]);

  useEffect(() => {
    if (parentTicket?.id) {
      refreshChildren();
    }
  }, [parentTicket?.id]);

  const openCreate = () => {
    setEditingSub(null);
    setFormTitle('');
    setFormDescription('');
    setFormAssigneeId('');
    const firstP = priorities[0];
    const firstS = statuses[0];
    setFormPriorityId(firstP ? String(firstP.id) : '');
    setFormStatusId(firstS ? String(firstS.id) : '');
    loadUsersForProject();
    setModalOpen(true);
  };

  const assigneeOptions = useMemo(
    () =>
      availableUsers.map((u) => ({
        value: u.id,
        label: userLabel(u),
      })),
    [availableUsers]
  );

  const priorityOptions = useMemo(
    () => priorities.map((p) => ({ value: p.id, label: p.priority_name })),
    [priorities]
  );

  const statusOptions = useMemo(
    () => statuses.map((s) => ({ value: s.id, label: s.status_name })),
    [statuses]
  );

  const openEdit = async () => {
    const sub = rows.find((r) => r.id === selectedRowId);
    if (!sub) return;
    setEditingSub(sub);
    setFormTitle(sub.title || '');
    setFormDescription(sub.description || '');
    const pid = sub.priority?.id;
    const sid = sub.status?.id;
    setFormPriorityId(pid ? String(pid) : (priorities[0] ? String(priorities[0].id) : ''));
    setFormStatusId(sid ? String(sid) : (statuses[0] ? String(statuses[0].id) : ''));
    const aid = sub.assigned_to_user?.id;
    setFormAssigneeId(aid ? String(aid) : '');
    loadUsersForProject();
    setModalOpen(true);
    try {
      const res = await ticketsAPI.getTicket(sub.id);
      const full = ticketFromResponse(res);
      if (full) {
        setFormDescription(full.description || '');
        setEditingSub(full);
        const assignId = full.assigned_to_user?.id ?? full.assigned_to_user_id;
        setFormAssigneeId(assignId ? String(assignId) : '');
      }
    } catch {
      /* keep row data */
    }
  };

  const handleSaveSub = async (e) => {
    e.preventDefault();
    if (!formTitle.trim() || !projectId || !parentTicket?.id) return;
    const pr = parseInt(formPriorityId, 10);
    const st = parseInt(formStatusId, 10);
    if (Number.isNaN(pr) || Number.isNaN(st)) {
      setError(t('subTicketFormIncomplete'));
      return;
    }
    setSaving(true);
    try {
      const assignPayload = formAssigneeId
        ? { assigned_to_user_id: parseInt(formAssigneeId, 10) }
        : { assigned_to_user_id: null };

      if (editingSub) {
        const res = await ticketsAPI.updateTicket(editingSub.id, {
          title: formTitle.trim(),
          description: formDescription.trim() || '',
          priority_id: pr,
          status_id: st,
          ...assignPayload,
        });
        if (!res?.success && !res?.id) {
          throw new Error(res?.message || t('errorOccurred'));
        }
      } else {
        const body = {
          title: formTitle.trim(),
          description: formDescription.trim() || '',
          project: projectId,
          status_id: st,
          priority_id: pr,
          parent_ticket_id: parentTicket.id,
          ...assignPayload,
        };
        const res = await ticketsAPI.createTicket(body);
        if (!res?.success && !res?.id) {
          throw new Error(res?.message || t('errorOccurred'));
        }
      }
      setModalOpen(false);
      await refreshChildren();
    } catch (err) {
      setError(err.message || t('errorOccurred'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const sub = rows.find((r) => r.id === selectedRowId);
    if (!sub) return;
    if (!window.confirm(t('subTicketDeleteConfirm'))) return;
    try {
      await ticketsAPI.deleteTicket(sub.id);
      setSelectedRowId(null);
      await refreshChildren();
    } catch (err) {
      setError(err.message || t('deleteError'));
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'title',
        header: t('subTicketColTitle'),
        cell: ({ getValue }) => (
          <span className="font-medium text-gray-900 dark:text-white">{getValue() || '-'}</span>
        ),
      },
      {
        id: 'status',
        header: t('subTicketColStatus'),
        accessorFn: (row) => row.status?.status_name,
        cell: ({ getValue }) => (
          <span className="text-gray-600 dark:text-gray-300">{getValue() || '-'}</span>
        ),
      },
      {
        id: 'priority',
        header: t('subTicketColPriority'),
        accessorFn: (row) => row.priority?.priority_name,
        cell: ({ getValue }) => (
          <span className="text-gray-600 dark:text-gray-300">{getValue() || '-'}</span>
        ),
      },
      {
        id: 'assignee',
        header: t('subTicketColAssignee'),
        accessorFn: (row) => {
          const u = row.assigned_to_user;
          if (!u) return '';
          return `${u.first_name || ''} ${u.last_name || ''}`.trim();
        },
        cell: ({ getValue }) => (
          <span className="text-gray-600 dark:text-gray-300">{getValue() || t('unassigned')}</span>
        ),
      },
      {
        accessorKey: 'due_date',
        header: t('subTicketColDue'),
        cell: ({ getValue }) => {
          const v = getValue();
          if (!v) return <span className="text-gray-500 dark:text-gray-400">-</span>;
          return (
            <span className="text-gray-600 dark:text-gray-300">
              {new Date(v).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
            </span>
          );
        },
      },
    ],
    [t]
  );

  const table = useReactTable({
    data: filteredRows,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="p-6 space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded dark:bg-red-900/20 dark:border-red-800 dark:text-red-300 flex justify-between items-start">
          <span className="text-sm">{error}</span>
          <button type="button" onClick={() => setError(null)} className="ml-2 text-red-500 hover:text-red-700" aria-label="Dismiss">
            ×
          </button>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <input
            type="search"
            value={tableSearch}
            onChange={(e) => setTableSearch(e.target.value)}
            placeholder={t('subTicketSearchPlaceholder')}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 pl-9 text-sm text-gray-900 placeholder:text-gray-400 focus:border-highlight-500 focus:outline-none focus:ring-1 focus:ring-highlight-500/30 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder:text-gray-500"
            aria-label={t('subTicketSearchPlaceholder')}
          />
        </div>
      </div>

      <ActionToolbar>
        <IconActionButton icon={PlusIcon} onClick={openCreate} title={t('subTicketNew')} />
        <IconActionButton
          icon={PencilSquareIcon}
          onClick={openEdit}
          title={t('subTicketEdit')}
          disabled={!selectedRowId}
        />
        <IconActionButton
          icon={TrashIcon}
          onClick={handleDelete}
          title={t('delete')}
          disabled={!selectedRowId}
        />
      </ActionToolbar>

      {loading && (
        <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-300 text-sm">
          {t('loading')}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700 rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900/50">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                  >
                    {header.column.getCanSort() ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 cursor-pointer select-none group hover:bg-gray-100 dark:hover:bg-gray-800 rounded px-1 -mx-1"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {{
                          asc: <ArrowUpIcon className="h-3 w-3" />,
                          desc: <ArrowDownIcon className="h-3 w-3" />,
                        }[header.column.getIsSorted()] ?? <ArrowsUpDownIcon className="h-3 w-3 opacity-40" />}
                      </button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-3 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  {t('noRecordsFound')}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  onClick={() => setSelectedRowId(row.original.id)}
                  onDoubleClick={() => onOpenChildTicket && onOpenChildTicket(row.original)}
                  className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                    selectedRowId === row.original.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                  }`}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-3 py-4 whitespace-nowrap text-xs text-gray-900 dark:text-gray-100">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <ResponsiveModal
        isOpen={modalOpen}
        onClose={() => !saving && setModalOpen(false)}
        overlayClassName="z-[10050]"
      >
        <div className="flex flex-col max-h-[88vh]">
          <div className="flex items-center justify-between px-4 pt-5 pb-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">
              {editingSub ? t('subTicketEdit') : t('subTicketNew')}
            </h2>
            <button
              type="button"
              onClick={() => !saving && setModalOpen(false)}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
          <form onSubmit={handleSaveSub} className="overflow-y-auto px-4 sm:p-6 space-y-4">
            <div className="space-y-1">
              <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">{t('subTicketTitle')} *</Label>
              <input
                type="text"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-highlight-500 focus:outline-none focus:ring-1 focus:ring-highlight-500/30 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder:text-gray-500"
                required
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">{t('editDescription')}</Label>
              <textarea
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                rows={3}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-highlight-500 focus:outline-none focus:ring-1 focus:ring-highlight-500/30 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder:text-gray-500"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">{t('allPriorities')}</Label>
                <Select
                  classNamePrefix="subticket-select"
                  options={priorityOptions}
                  value={priorityOptions.find((o) => String(o.value) === formPriorityId) || null}
                  onChange={(opt) => setFormPriorityId(opt ? String(opt.value) : '')}
                  isSearchable
                  isClearable={false}
                  placeholder={t('allPriorities')}
                  isDisabled={saving || priorityOptions.length === 0}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  menuPosition="fixed"
                  styles={subTicketSelectStyles}
                  classNames={subTicketSelectClassNames}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">{t('allStatuses')}</Label>
                <Select
                  classNamePrefix="subticket-select"
                  options={statusOptions}
                  value={statusOptions.find((o) => String(o.value) === formStatusId) || null}
                  onChange={(opt) => setFormStatusId(opt ? String(opt.value) : '')}
                  isSearchable
                  isClearable={false}
                  placeholder={t('allStatuses')}
                  isDisabled={saving || statusOptions.length === 0}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  menuPosition="fixed"
                  styles={subTicketSelectStyles}
                  classNames={subTicketSelectClassNames}
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">{t('assignee')}</Label>
              <Select
                classNamePrefix="subticket-select"
                options={assigneeOptions}
                value={assigneeOptions.find((o) => String(o.value) === formAssigneeId) || null}
                onChange={(opt) => setFormAssigneeId(opt ? String(opt.value) : '')}
                isSearchable
                isClearable
                placeholder={t('assignee')}
                noOptionsMessage={() => t('noRecordsFound')}
                isDisabled={loadingUsers || saving}
                isLoading={loadingUsers}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                menuPosition="fixed"
                styles={subTicketSelectStyles}
                classNames={subTicketSelectClassNames}
              />
            </div>
            <div className="mt-6 flex items-center justify-end space-x-3 border-t border-gray-100 dark:border-gray-700 pt-4">
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)} disabled={saving}>
                {t('cancel')}
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white" disabled={saving}>
                {saving ? t('saving') : t('save')}
              </Button>
            </div>
          </form>
        </div>
      </ResponsiveModal>
    </div>
  );
};

export default SubTicketsTab;
